import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Container } from './PaperAirplaneAnimation.styled';
import PaperAirplane from './PaperAirplane';
import Confetti from './Confetti';

// Festive colors for confetti and airplanes
const FESTIVE_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#FFA07A', // Light Salmon
  '#98D8C8', // Mint
  '#F7DC6F', // Yellow
  '#BB8FCE', // Purple
  '#85C1E2', // Sky Blue
  '#F8B739', // Orange
  '#E74C3C', // Bright Red
  '#3498DB', // Bright Blue
  '#2ECC71', // Green
];

/**
 * Convert hex color to HSL for CSS filter manipulation
 */
const hexToHsl = hex => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }

  return {
    hue: h * 360,
    saturation: s * 100,
    lightness: l * 100,
  };
};

/**
 * PaperAirplaneAnimation - A reusable background animation component
 * that displays fireworks effect with confetti and paper airplanes exploding outward.
 *
 * @param {number} confettiCount - Number of confetti pieces per explosion (default: 50)
 * @param {number} airplaneCount - Number of airplanes per explosion (default: 10)
 * @param {number} explosionCount - Number of simultaneous explosions on mount (default: 3, use 0 for manual-only mode)
 * @param {number} speed - Base speed multiplier (default: 1)
 * @param {number} explosionInterval - Time between new explosions in ms (default: 2000, use a very large number like Infinity to disable)
 * @param {boolean} manualOnly - If true, disables all automatic explosions (explosionCount=0, explosionInterval=Infinity)
 * @param {string} className - Additional CSS class name
 */
const PaperAirplaneAnimation = forwardRef(
  (
    {
      confettiCount = 100,
      airplaneCount = 60,
      explosionCount = 3,
      speed = 1,
      explosionInterval = 2000,
      manualOnly = false,
      className,
    },
    ref
  ) => {
    // If manualOnly is true, override explosion settings
    const effectiveExplosionCount = manualOnly ? 0 : explosionCount;
    const effectiveExplosionInterval = manualOnly
      ? Infinity
      : explosionInterval;
    const containerRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastExplosionTimeRef = useRef(0);

    // Create a new explosion at specific position or random
    const createExplosion = useCallback(
      (explosionX = null, explosionY = null) => {
        const containerWidth = window.innerWidth;
        const containerHeight = window.innerHeight;

        // Use provided position or random explosion point (prefer upper portion of screen)
        const x =
          explosionX !== null
            ? explosionX
            : containerWidth * 0.2 + Math.random() * containerWidth * 0.6;
        const y =
          explosionY !== null
            ? explosionY
            : containerHeight * 0.1 + Math.random() * containerHeight * 0.3;

        const confettiParticles = Array.from({ length: confettiCount }, () => {
          const color =
            FESTIVE_COLORS[Math.floor(Math.random() * FESTIVE_COLORS.length)];

          // Random angle for explosion
          const angle = Math.random() * Math.PI * 2;
          const velocityMagnitude = 2 + Math.random() * 4; // Speed between 2 and 6
          const velocityX = Math.cos(angle) * velocityMagnitude * speed;
          const velocityY = Math.sin(angle) * velocityMagnitude * speed;

          return {
            id: Math.random(),
            x: x,
            y: y,
            rotation: Math.random() * 360,
            rotationSpeed: (Math.random() - 0.5) * 20, // Increased from 8 to 20 for more rotation
            velocityX,
            velocityY,
            size: 0.5 + Math.random() * 0.5,
            opacity: 1, // Start fully visible
            color,
            age: 0, // Track age for fade-out
            maxAge: 1000 + Math.random() * 1000, // Fade out after 1-2 seconds
          };
        });

        const airplaneParticles = Array.from({ length: airplaneCount }, () => {
          // Random angle for explosion - airplanes start with explosion velocity
          const angle = Math.random() * Math.PI * 2;
          const initialVelocityMagnitude = 2 + Math.random() * 4;
          const velocityX = Math.cos(angle) * initialVelocityMagnitude * speed;
          const velocityY = Math.sin(angle) * initialVelocityMagnitude * speed;

          const initialRotation =
            Math.atan2(velocityY, velocityX) * (180 / Math.PI);

          // Random color for airplane
          const colorHex =
            FESTIVE_COLORS[Math.floor(Math.random() * FESTIVE_COLORS.length)];
          const colorHsl = hexToHsl(colorHex);

          return {
            id: Math.random(),
            x: x,
            y: y,
            rotation: initialRotation,
            rotationSpeed: (Math.random() - 0.5) * 15, // Increased from 6 to 15 for more rotation
            velocityX,
            velocityY,
            targetRotation: initialRotation,
            size: 0.2 + Math.random() * 0.4, // Size range: 0.3 to 0.7
            opacity: 1,
            color: colorHsl,
            age: 0,
            maxAge: 3000 + Math.random() * 2000, // Longer lifespan: 3-5 seconds
            phase: 'exploding', // Start in explosion phase, then transition to flying
            turnDirection: Math.random() > 0.5 ? 1 : -1, // Random turn direction for smooth curves
          };
        });

        return { confettiParticles, airplaneParticles };
      },
      [confettiCount, airplaneCount, speed]
    );

    // Expose trigger method via ref
    useImperativeHandle(
      ref,
      () => ({
        triggerExplosion: (x, y) => {
          const newExplosion = createExplosion(x, y);
          setConfetti(prev => [...prev, ...newExplosion.confettiParticles]);
          setAirplanes(prev => [...prev, ...newExplosion.airplaneParticles]);
        },
      }),
      [createExplosion]
    );

    const [confetti, setConfetti] = useState(() => {
      const explosions = [];
      for (let i = 0; i < effectiveExplosionCount; i++) {
        explosions.push(createExplosion());
      }
      return explosions.flatMap(e => e.confettiParticles);
    });

    const [airplanes, setAirplanes] = useState(() => {
      const explosions = [];
      for (let i = 0; i < effectiveExplosionCount; i++) {
        explosions.push(createExplosion());
      }
      return explosions.flatMap(e => e.airplaneParticles);
    });

    useEffect(() => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      let containerWidth = container.offsetWidth || window.innerWidth;
      let containerHeight = container.offsetHeight || window.innerHeight;

      const handleResize = () => {
        containerWidth = container.offsetWidth || window.innerWidth;
        containerHeight = container.offsetHeight || window.innerHeight;
      };

      window.addEventListener('resize', handleResize);

      let lastTime = performance.now();

      const animate = currentTime => {
        // Create new explosions periodically (skip if interval is Infinity, indicating manual-only mode)
        if (
          effectiveExplosionInterval !== Infinity &&
          currentTime - lastExplosionTimeRef.current >
            effectiveExplosionInterval
        ) {
          lastExplosionTimeRef.current = currentTime;
          const newExplosion = createExplosion();

          setConfetti(prev => [...prev, ...newExplosion.confettiParticles]);
          setAirplanes(prev => [...prev, ...newExplosion.airplaneParticles]);
        }

        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;

        // Animate confetti
        setConfetti(prevConfetti => {
          return prevConfetti
            .map(piece => {
              let {
                x,
                y,
                rotation,
                velocityX,
                velocityY,
                rotationSpeed,
                age,
                maxAge,
                opacity,
              } = piece;

              // Update position
              x += velocityX;
              y += velocityY;
              rotation += rotationSpeed;
              age += deltaTime; // Use actual frame time

              // Apply gravity (slow down vertical velocity)
              velocityY += 0.1 * speed; // Gravity effect

              // Fade out as particle ages
              opacity = Math.max(0, 1 - age / maxAge);

              // Remove particles that are too old or off screen
              if (
                age > maxAge ||
                x < -50 ||
                x > containerWidth + 50 ||
                y > containerHeight + 50
              ) {
                return null;
              }

              return {
                ...piece,
                x,
                y,
                rotation,
                velocityX,
                velocityY,
                opacity,
                age,
              };
            })
            .filter(piece => piece !== null);
        });

        // Animate airplanes
        setAirplanes(prevAirplanes => {
          return prevAirplanes
            .map(airplane => {
              let {
                x,
                y,
                rotation,
                rotationSpeed,
                velocityX,
                velocityY,
                targetRotation,
                age,
                maxAge,
                opacity,
                phase,
                turnDirection,
              } = airplane;

              // Update position
              x += velocityX;
              y += velocityY;
              age += deltaTime;

              // Add continuous rotation (like confetti spinning)
              rotation += rotationSpeed * (deltaTime / 16) * 1.5; // Increased multiplier for more rotation

              // Transition from explosion phase to flying phase after initial burst
              if (phase === 'exploding' && age > 300) {
                phase = 'flying';
              }

              if (phase === 'exploding') {
                // Initial explosion phase: fast outward movement
                // Calculate target rotation based on velocity direction
                const angle =
                  Math.atan2(velocityY, velocityX) * (180 / Math.PI);
                targetRotation = angle;

                // Smoothly interpolate rotation towards target (combined with spinning)
                let rotationDiff = targetRotation - rotation;
                while (rotationDiff > 180) rotationDiff -= 360;
                while (rotationDiff < -180) rotationDiff += 360;
                rotation += rotationDiff * 0.4; // Increased from 0.2 to 0.4 for more responsive rotation

                // Apply slight gravity
                velocityY += 0.05 * speed;
              } else {
                // Flying phase: smooth paper airplane movement
                // Calculate target rotation based on velocity direction
                const angle =
                  Math.atan2(velocityY, velocityX) * (180 / Math.PI);
                targetRotation = angle;

                // Smoothly interpolate rotation towards target (combined with spinning)
                let rotationDiff = targetRotation - rotation;
                while (rotationDiff > 180) rotationDiff -= 360;
                while (rotationDiff < -180) rotationDiff += 360;
                rotation += rotationDiff * 0.15; // Increased from 0.05 to 0.15 for more responsive rotation

                // Add smooth curving motion (like gliding)
                const turnAmount = turnDirection * 0.02 * speed;
                const currentSpeed = Math.sqrt(
                  velocityX * velocityX + velocityY * velocityY
                );
                if (currentSpeed > 0.1) {
                  // Apply slight turn perpendicular to velocity
                  const perpX = -velocityY / currentSpeed;
                  const perpY = velocityX / currentSpeed;
                  velocityX += perpX * turnAmount;
                  velocityY += perpY * turnAmount;
                }

                // Apply gravity (gradual descent)
                velocityY += 0.08 * speed;

                // Slow down gradually (air resistance)
                velocityX *= 0.998;
                velocityY *= 0.998;
              }

              // Fade out as particle ages (faster fade in last 30% of lifetime)
              const fadeStart = maxAge * 0.7;
              if (age > fadeStart) {
                const fadeProgress = (age - fadeStart) / (maxAge - fadeStart);
                opacity = Math.max(0, 1 - fadeProgress);
              } else {
                opacity = 1;
              }

              // Remove particles that are too old or off screen
              if (
                age > maxAge ||
                x < -100 ||
                x > containerWidth + 100 ||
                y > containerHeight + 100
              ) {
                return null;
              }

              return {
                ...airplane,
                x,
                y,
                rotation,
                velocityX,
                velocityY,
                targetRotation,
                opacity,
                age,
                phase,
              };
            })
            .filter(airplane => airplane !== null);
        });

        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animationFrameRef.current = requestAnimationFrame(animate);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, [speed, effectiveExplosionInterval, createExplosion]);

    return (
      <Container ref={containerRef} className={className}>
        {/* Render confetti */}
        {confetti.map(piece => (
          <Confetti
            key={piece.id}
            x={piece.x}
            y={piece.y}
            rotation={piece.rotation}
            size={piece.size}
            opacity={piece.opacity}
            color={piece.color}
          />
        ))}

        {/* Render airplanes */}
        {airplanes.map(airplane => (
          <PaperAirplane
            key={airplane.id}
            x={airplane.x}
            y={airplane.y}
            rotation={airplane.rotation}
            size={airplane.size}
            opacity={airplane.opacity}
            color={airplane.color}
          />
        ))}
      </Container>
    );
  }
);

PaperAirplaneAnimation.displayName = 'PaperAirplaneAnimation';

export default PaperAirplaneAnimation;
